import { getApiFieldName, getDbFieldName } from './dynamicColumns';
import { PRO_COLUMN_IDENTITIES } from './proColumnIdentities';

/**
 * A column id's REGISTRY IDENTITY: the `field_definitions.fieldname`(s) whose data
 * that column renders.
 *
 * The same field wears three different column ids across the app, because three
 * layers named it independently:
 *
 *   `billtoname`   the raw CMS fieldname — the LC drill grids key columns by it
 *                  (LCDashboards' buildDrillExtraColumns), because the bespoke
 *                  drill endpoints emit it as the row key.
 *   `billto_name`  the API name — `FIELD_NAME_MAPPING` remaps seven fields on the
 *                  way out, and every registry-driven grid (dispatch + LC bucket
 *                  cards) ids its columns by the result.
 *   `billToName`   the hand-written id of the static PRO column set
 *                  (proCardColumns), shared by Search Results and the LC
 *                  Dashboards card/drill view.
 *
 * For the seven remapped fields the three only differ in case and punctuation, so
 * normalising closes the gap. For the ~19 PRO columns that were named for what
 * they MEAN rather than where they come from (`terminal`/`rail`,
 * `lfd`/`lastfreedaydate`, `deliverTo`/`destname`) nothing derivable connects the
 * two — the host has to say so, which is what this map is.
 *
 * An EMPTY array is meaningful and distinct from an absent entry: it declares that
 * a column has NO registry identity, so it is matched by its exact id and never by
 * resemblance. That is the `meta: { registryTitle: false }` case — a column whose
 * id collides with a registry fieldname while holding different data (the LC
 * Resolution board's `createdby` is the reschedule's author, not the order's; CST's
 * `bill_to` is the bill-to NAME where the registry's `billto` is the number).
 */
export type FieldAliasMap = Readonly<Record<string, readonly string[]>>;

/**
 * Normalise a column id / fieldname for comparison: case and punctuation are noise
 * when matching `billToName` against `billto_name` against `billtoname`.
 *
 * Shared with proCardColumns' registry-title resolution deliberately — a column
 * inherits a definition's NAME under exactly the rules that make a saved layout
 * resolve onto it, so the two must not drift.
 */
export const normFieldName = (v: string): string => v.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The normalised keys a column id can be matched on.
 *
 * A declared alias is authoritative — including a declared EMPTY one, which yields
 * no keys at all and so leaves the column matchable only by its exact id. With
 * nothing declared, the id is run back through the API remap and normalised, which
 * is what collapses the `billtoname` / `billto_name` / `billToName` family without
 * any host having to declare it.
 */
export function canonicalFieldKeys(id: string, aliases?: FieldAliasMap): string[] {
  const declared = aliases?.[id];
  if (declared) return declared.map(normFieldName);
  // Nothing declared: fall back to the one id vocabulary in this app that cannot be
  // derived, before giving up and reading the id as its own name. This is what lets
  // a layout move in BOTH directions — a grid that names its columns after the
  // fields has no reason to know that `available` means `availablegoodtogodatetime`,
  // and only the side that invented the id ever did. Reached only when neither grid
  // declared an identity for it, so an explicit declaration — including the empty
  // one that means "match me by id alone" — still wins.
  const known = PRO_COLUMN_IDENTITIES[id];
  if (known) return known.map(normFieldName);
  return [normFieldName(getDbFieldName(id))];
}

/** Every key a registry field can be reached under, both spellings normalised. */
export function fieldnameKeys(fieldname: string): string[] {
  const api = normFieldName(getApiFieldName(fieldname));
  const raw = normFieldName(fieldname);
  return api === raw ? [raw] : [raw, api];
}
